import { ShadowStyle } from 'client/components/core/Sidebar/components/Settings/styles'
import Border from 'client/components/core/Sidebar/components/Settings/styles/Border'
import { ImagePlacementVariants } from 'common/types/entities/LatestBlogPostsCarouselInterface'
import React from 'react'
import { useTypedTranslation } from 'src/shared/i18next/hooks/use-typed-translation'
import { GroupTitle } from 'src/editor/layout/sidebar/settings/group-title'
import { ColorPickerControl } from 'src/editor/shared/settings/controls/color-picker/color-picker-control'
import { PaddingGroup } from 'src/editor/shared/settings/groups/padding'
import {
  OptionSingleValueType,
  SelectNew,
} from 'src/editor/shared/ui-lib/select-new/select-new'
import { LayoutType } from '../PostListingLayoutSetting/utils'
import VerticalAlign from './VerticalAlign'
import { useUpdatePropNew } from '../../hooks/useUpdateProps'
import { BlogPostListingInterface } from 'common/types/entities/BlogPostListingInterface'
import { MakeRequired } from 'common/types/utilityTypes'

type BlogPostCardSettingsProps = {
  entity: BlogPostListingInterface
  isMobile: boolean
}

function BlogPostCardSettings({ isMobile, entity }: BlogPostCardSettingsProps) {
  const { t } = useTypedTranslation()

  const updateProp = useUpdatePropNew(
    entity as MakeRequired<BlogPostListingInterface, 'postCard'>,
  )

  const isImagePlacementSelectVisible = isMobile
    ? true
    : !entity.layoutType || entity.layoutType === LayoutType.Grid3x3

  const isImageVariantIsBackground = isMobile
    ? entity.mobileImagePlacement === ImagePlacementVariants.Background
    : entity.imagePlacement === ImagePlacementVariants.Background ||
      (entity.layoutType && entity.layoutType !== LayoutType.Grid3x3)

  const imagePlacementOptions = [
    {
      label: t(
        'entity_settings.latest_blog_posts_carousel.image_placement.top',
      ),
      value: ImagePlacementVariants.Up,
    },
    {
      label: t(
        'entity_settings.latest_blog_posts_carousel.image_placement.left',
      ),
      value: ImagePlacementVariants.Left,
    },
    {
      label: t(
        'entity_settings.latest_blog_posts_carousel.image_placement.background',
      ),
      value: ImagePlacementVariants.Background,
    },
  ]

  function updateImagePlacement(
    option: OptionSingleValueType<ImagePlacementVariants>,
  ) {
    if (option) {
      if (isMobile) {
        updateProp('mobileImagePlacement')(option.value)
      } else updateProp('imagePlacement')(option.value)
    }
  }

  const getDefaultImagePlacement = (imagePlacement?: string) => {
    if (!imagePlacement) {
      return imagePlacementOptions[0]
    }

    const selectedCategoryOption = imagePlacementOptions.find(
      state => state.value === imagePlacement,
    )

    if (selectedCategoryOption) {
      return selectedCategoryOption
      // biome-ignore lint/style/noUselessElse: <explanation>
    } else return imagePlacementOptions[0]
  }
  const defaultForeGroundColor = 'rgba(255, 255, 255, 0.2)'

  const foreGroundColor =
    entity.postCard?.foregroundColor || defaultForeGroundColor
  const mobileForeGroundColor =
    entity.postCard?.mobileForegroundColor || defaultForeGroundColor

  return (
    <>
      <GroupTitle>entity_settings.blog_post_listing.post_card.title</GroupTitle>
      {isImagePlacementSelectVisible && (
        <SelectNew
          labelText="entity_settings.latest_blog_posts_carousel.image_placement.label"
          selectedOption={
            isMobile
              ? getDefaultImagePlacement(entity.mobileImagePlacement)
              : getDefaultImagePlacement(entity.imagePlacement)
          }
          onChange={updateImagePlacement}
          options={imagePlacementOptions}
          isMulti={false}
        />
      )}
      {isImageVariantIsBackground && (
        <VerticalAlign
          align={
            isMobile
              ? entity.postCard?.mobileVerticalAlignment
              : entity.postCard?.verticalAlignment
          }
          update={
            isMobile
              ? updateProp('postCard', 'mobileVerticalAlignment')
              : updateProp('postCard', 'verticalAlignment')
          }
        />
      )}
      <ColorPickerControl
        update={updateProp('postCard', 'backgroundColor')}
        mobileUpdate={updateProp('postCard', 'mobileBackgroundColor')}
        color={entity.postCard?.backgroundColor}
        mobileColor={entity.postCard?.mobileBackgroundColor}
        label="settings_styles.background_color.label"
      />
      {isImageVariantIsBackground && (
        <ColorPickerControl
          update={updateProp('postCard', 'foregroundColor')}
          mobileUpdate={updateProp('postCard', 'mobileForegroundColor')}
          color={foreGroundColor}
          mobileColor={mobileForeGroundColor}
          label="entity_settings.blog_post_listing.foreground_color"
        />
      )}
      <ShadowStyle
        shadow={entity.postCard?.boxShadow}
        mobileShadow={entity.postCard?.mobileBoxShadow}
        update={updateProp('postCard', 'boxShadow')}
        mobileUpdate={updateProp('postCard', 'mobileBoxShadow')}
        isTitleNeeded={false}
        labelText="settings_styles.shadow.label"
      />
      <Border
        showGroupTitle={false}
        update={updateProp('postCard', 'border')}
        mobileUpdate={updateProp('postCard', 'mobileBorder')}
        border={entity.postCard?.border}
        mobileBorder={entity.postCard?.mobileBorder}
      />
      <PaddingGroup
        padding={
          isMobile ? entity.postCard?.mobilePadding : entity.postCard?.padding
        }
        update={updateProp('postCard', isMobile ? 'mobilePadding' : 'padding')}
        label="settings_styles.padding.label"
      />
    </>
  )
}

export default BlogPostCardSettings
